/** Small reusable UI builders shared by every view. */

import { h } from '../../utils/dom.js';

export function card(children, { title = null, step = null, className = '' } = {}) {
  return h('section', { class: `card ${className}`.trim() }, [
    title ? h('div', { class: 'card__head' }, [h('div', { class: 'card__title' }, [step ? h('span', { class: 'step-badge' }, [String(step)]) : null, title])]) : null,
    ...(Array.isArray(children) ? children : [children]),
  ]);
}

export function note(message, kind = 'info', icon = null) {
  const icons = { info: '\u2139', warn: '\u26a0', danger: '\u2716', ok: '\u2714' };
  return h('div', { class: `note note--${kind}` }, [
    h('span', { class: 'note__icon', 'aria-hidden': 'true' }, [icon ?? icons[kind] ?? icons.info]),
    h('div', {}, [message]),
  ]);
}

export function button(label, { kind = 'ghost', onClick, block = false, large = false, small = false, disabled = false, title = null, id = null } = {}) {
  const classes = ['btn', `btn--${kind}`];
  if (block) classes.push('btn--block');
  if (large) classes.push('btn--lg');
  if (small) classes.push('btn--sm');
  return h(
    'button',
    {
      class: classes.join(' '),
      type: 'button',
      id,
      title,
      disabled: disabled || undefined,
      onclick: onClick,
    },
    [label],
  );
}

export function linkButton(label, href, { kind = 'ghost', block = false, title = null } = {}) {
  return h(
    'a',
    {
      class: `btn btn--${kind}${block ? ' btn--block' : ''}`,
      href,
      target: '_blank',
      rel: 'noopener noreferrer',
      title,
    },
    [label],
  );
}

/**
 * Segmented option picker. Touch friendly (44px min), works without hover.
 * @param {Array<{id:string,label:string,hint?:string}>} options
 */
export function segmented(options, current, onSelect) {
  const container = h('div', { class: 'segmented', role: 'group' });
  for (const option of options) {
    const el = h(
      'button',
      {
        class: 'segmented__option',
        type: 'button',
        'aria-pressed': String(option.id === current),
        onclick: () => onSelect(option.id),
      },
      [
        h('span', {}, [option.label]),
        option.hint ? h('span', { class: 'segmented__hint' }, [option.hint]) : null,
      ],
    );
    container.append(el);
  }
  return container;
}

export function kv(pairs) {
  const dl = h('dl', { class: 'kv' });
  for (const [key, value] of pairs) {
    if (value === null || value === undefined || value === '') continue;
    dl.append(h('dt', {}, [key]), h('dd', {}, [value instanceof Node ? value : String(value)]));
  }
  return dl;
}

export function chipRow(items) {
  return h('div', { class: 'chip-row' }, items.filter(Boolean).map((item) => (typeof item === 'string' ? h('span', { class: 'chip' }, [item]) : item)));
}

export function progressBar(value, { indeterminate = false } = {}) {
  return h('div', { class: 'progress' }, [
    h('div', {
      class: `progress__bar${indeterminate ? ' progress__bar--indeterminate' : ''}`,
      style: indeterminate ? {} : { width: `${Math.round(Math.min(1, Math.max(0, value)) * 100)}%` },
    }),
  ]);
}

export function emptyState(title, message, action = null) {
  return h('div', { class: 'stack', style: { textAlign: 'center', padding: '28px 12px' } }, [
    h('h3', {}, [title]),
    h('p', { class: 'muted small' }, [message]),
    action ? h('div', { style: { display: 'flex', justifyContent: 'center' } }, [action]) : null,
  ]);
}

export function sectionHeading(title, subtitle = null, right = null) {
  return h('div', { class: 'between', style: { marginBottom: '4px' } }, [
    h('div', {}, [h('h2', {}, [title]), subtitle ? h('p', { class: 'dim small', style: { margin: 0 } }, [subtitle]) : null]),
    right,
  ]);
}

/** Render a waveform peak array into a strip. */
export function waveform(peaks) {
  return h(
    'div',
    { class: 'waveform', 'aria-hidden': 'true' },
    Array.from(peaks).map((value) => h('span', { class: 'waveform__bar', style: { height: `${Math.max(3, value * 100)}%` } })),
  );
}
